import { useState, useEffect } from 'react'
import { useApi } from '../../../shared/hooks/useApi'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'
import { PieChart as PieChartIcon, DollarSign, FileText, RefreshCw } from 'lucide-react'

interface CPVData {
  cpv_code: string
  cpv_name: string
  total_value: number
  contract_count: number
  rank: number
}

interface CPVAnalysisProps {
  year?: number
  onCPVClick?: (cpvCode: string) => void
  onRefresh?: () => void
}

const COLORS = [
  '#4A8A78',
  '#6A9E8E',
  '#9A7020',
  '#6B5EA0',
  '#2D7A40',
  '#A07008',
  '#C04030',
  '#7BA396',
  '#B8924D',
  '#8B7DB8',
]

const CPVAnalysis = ({ year, onCPVClick, onRefresh }: CPVAnalysisProps) => {
  const { getTopCPV } = useApi()
  const [data, setData] = useState<CPVData[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [level, setLevel] = useState<2 | 3>(2) // 2=Division, 3=Group

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)
        const results = await getTopCPV(year, level)
        setData(results)
      } catch (err) {
        console.error('Failed to load CPV stats:', err)
        setError(err instanceof Error ? err.message : 'Unknown error')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [year, level, getTopCPV])

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
      </div>
    )
  }

  if (error) {
    return <div className="text-danger p-4">Błąd: {error}</div>
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-ink-muted p-8 text-center">
        <p className="mb-4">Brak danych dla wybranych kryteriów.</p>
        <button
          onClick={onRefresh}
          className="inline-flex items-center px-4 py-2 bg-brand text-white rounded-md hover:bg-brand-hover transition-colors"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Przelicz statystyki
        </button>
      </div>
    )
  }

  const chartData = data.slice(0, 10).map((item) => ({
    name: item.cpv_code,
    fullName: item.cpv_name,
    wartość: item.total_value,
    kontrakty: item.contract_count,
  }))

  return (
    <div className="space-y-6">
      <div className="flex justify-end space-x-2 mb-4">
        <span className="text-sm font-medium text-ink-secondary self-center">Poziom:</span>
        <button
          onClick={() => setLevel(2)}
          className={`px-3 py-1 rounded text-sm ${level === 2 ? 'bg-brand text-white' : 'bg-surface-inset text-ink-secondary'}`}
        >
          Działy (2 cyfry)
        </button>
        <button
          onClick={() => setLevel(3)}
          className={`px-3 py-1 rounded text-sm ${level === 3 ? 'bg-brand text-white' : 'bg-surface-inset text-ink-secondary'}`}
        >
          Grupy (3 cyfry)
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Value Chart */}
        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <h3 className="text-lg font-medium text-ink mb-4 flex items-center">
            <DollarSign className="h-5 w-5 mr-2" />
            Top 10 CPV wg Wartości
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis type="number" tickFormatter={(val) => `${(val / 1000000).toFixed(0)}M`} tick={{ fill: 'var(--color-ink-muted)' }} />
              <YAxis dataKey="name" type="category" width={50} tick={{ fill: 'var(--color-ink-muted)' }} />
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--color-surface-raised)', border: '1px solid var(--color-border)', color: 'var(--color-ink)' }}
                formatter={(value) =>
                  new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(
                    value as number,
                  )
                }
                labelFormatter={(label) =>
                  chartData.find((d) => d.name === label)?.fullName || label
                }
              />
              <Bar dataKey="wartość" fill="var(--color-brand)" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Distribution Pie Chart */}
        <div className="bg-surface-raised rounded-lg border border-border-subtle p-6">
          <h3 className="text-lg font-medium text-ink mb-4 flex items-center">
            <PieChartIcon className="h-5 w-5 mr-2" />
            Rozkład wartości kontraktów
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => (percent > 0.05 ? `${name}` : '')}
                outerRadius={80}
                fill="var(--color-brand)"
                dataKey="wartość"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{ backgroundColor: 'var(--color-surface-raised)', border: '1px solid var(--color-border)', color: 'var(--color-ink)' }}
                formatter={(value) =>
                  new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(
                    value as number,
                  )
                }
                labelFormatter={(label) =>
                  chartData.find((d) => d.name === label)?.fullName || label
                }
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Table */}
      <div className="bg-surface-raised rounded-lg border border-border-subtle overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center">
          <FileText className="h-5 w-5 mr-2 text-ink-muted" />
          <h3 className="text-lg font-medium text-ink">Ranking Kategorii CPV</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-surface">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Ranking
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Kod CPV
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Nazwa
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Wartość całkowita
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-ink-muted uppercase tracking-wider">
                  Liczba kontraktów
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface-raised divide-y divide-border">
              {data.map((item) => (
                <tr key={item.cpv_code} className="hover:bg-surface-hover">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink-muted">
                    #{item.rank}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-ink">
                    {item.cpv_code}
                  </td>
                  <td
                    className="px-6 py-4 text-sm text-ink-secondary cursor-pointer hover:text-brand hover:underline"
                    onClick={() => onCPVClick && onCPVClick(item.cpv_code)}
                  >
                    {item.cpv_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink text-right">
                    {new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(
                      item.total_value,
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-ink text-right">
                    {item.contract_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default CPVAnalysis
